import React from "react";
import { ChevronDown, X } from "lucide-react";

export type CatalogChipOption = { value: string; label: string };

/**
 * Compact oval toolbar chip used by the capability catalog and the other superadmin lists.
 * Filters get an "All" option and a clear button; pass `showAll={false}` for always-set
 * controls such as sort, which never show the active state.
 */
const CatalogFilterChip = ({
  label,
  value,
  options,
  onChange,
  disabled = false,
  showAll = true,
  allLabel = "All",
}: {
  label: string;
  value: string;
  options: CatalogChipOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  showAll?: boolean;
  allLabel?: string;
}) => {
  const active = showAll && Boolean(value);
  const fallback = showAll ? allLabel : options[0]?.label || "";
  const display = options.find((option) => option.value === value)?.label || fallback;
  const className = ["cap-catalog-chip", active ? "is-active" : "", disabled ? "is-disabled" : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={className}>
      <span className="cap-catalog-chip__label">{label}</span>
      <span className="cap-catalog-chip__value">{display}</span>
      {active ? (
        <button
          type="button"
          className="cap-catalog-chip__clear"
          aria-label={`Clear ${label} filter`}
          onClick={() => onChange("")}
          disabled={disabled}
        >
          <X size={12} strokeWidth={2.5} aria-hidden />
        </button>
      ) : (
        <ChevronDown className="cap-catalog-chip__chevron" size={13} strokeWidth={2} aria-hidden />
      )}
      <select
        className="cap-catalog-chip__select"
        aria-label={label}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      >
        {showAll ? <option value="">{allLabel}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default CatalogFilterChip;
